import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { API, useAuth } from "../App";
import { PageLayout } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Inbox, PencilLine, Send, Users as UsersIcon, Search, ArrowLeft,
  Loader2, ChevronDown, ChevronRight, Mail
} from "lucide-react";
import { RichTextEditor } from "../components/RichTextEditor";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const fmtFull = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

const Messages = () => {
  const { user } = useAuth();
  const location = useLocation();
  const canCompose = user && (user.role === "admin" || user.role === "instructor");

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const res = await fetch(`${API}/messages/threads`, { credentials: "include" });
      const data = await res.json();
      setThreads(data.threads || []);
    } catch (e) {
      toast.error("Couldn't load messages");
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  const loadThread = useCallback(async (tid) => {
    setLoadingThread(true);
    try {
      const res = await fetch(`${API}/messages/threads/${tid}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setThread(data.thread);
      setMessages(data.messages || []);
      // Update unread badge on the list
      setThreads((prev) => prev.map((t) => (t.thread_id === tid ? { ...t, unread_count: 0 } : t)));
      window.dispatchEvent(new Event("aspiritech:messages:refresh-unread"));
    } catch (e) {
      toast.error("Couldn't open thread");
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId, loadThread]);

  // Pre-open compose if redirected here with ?compose=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("compose") === "1" && canCompose) setComposeOpen(true);
  }, [location.search, canCompose]);

  const handleSent = async () => {
    await loadThreads();
    setComposeOpen(false);
    window.dispatchEvent(new Event("aspiritech:messages:refresh-unread"));
  };

  return (
    <PageLayout>
      <div className="space-y-4" data-testid="messages-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-1">Inbox</p>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">Messages</h1>
            <p className="text-slate-500 mt-1 text-sm">
              {canCompose
                ? "Reach out to students 1-on-1 or broadcast to an entire cohort."
                : "Conversations with your instructors and the Aspiritech team."}
            </p>
          </div>
          {canCompose && (
            <Button onClick={() => setComposeOpen(true)} className="btn-primary" data-testid="compose-btn">
              <PencilLine className="w-4 h-4 mr-1.5" /> Compose
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-[340px_1fr] gap-4 min-h-[60vh]">
          {/* Thread list */}
          <Card className={`border border-slate-200 ${selectedId ? "hidden md:block" : ""}`} data-testid="thread-list">
            <CardContent className="p-0">
              {loadingThreads ? (
                <div className="p-6 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : threads.length === 0 ? (
                <div className="p-8 text-center text-slate-500" data-testid="threads-empty">
                  <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm">No conversations yet.</p>
                  {canCompose && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setComposeOpen(true)}>
                      Start a conversation
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {threads.map((t) => (
                    <li key={t.thread_id}>
                      <button
                        onClick={() => setSelectedId(t.thread_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${
                          selectedId === t.thread_id ? "bg-[#FFD507]/10" : ""
                        }`}
                        data-testid={`thread-row-${t.thread_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-900 truncate text-sm">
                              {t.other_participants?.map((p) => p.name || p.email).join(", ") || "Conversation"}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{t.subject}</p>
                            <p className="text-xs text-slate-400 truncate mt-0.5">{t.last_snippet}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-[11px] text-slate-400">{fmtDate(t.last_message_at)}</p>
                            {t.unread_count > 0 && (
                              <span className="inline-block mt-1 bg-[#FFD507] text-[#0F1B3D] text-[10px] font-bold rounded-full px-1.5 min-w-[20px] text-center">
                                {t.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Thread view */}
          <Card className={`border border-slate-200 ${!selectedId ? "hidden md:flex md:items-center md:justify-center" : "flex flex-col"}`} data-testid="thread-view">
            {!selectedId ? (
              <div className="text-center text-slate-400 p-10">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Pick a conversation to view it.</p>
              </div>
            ) : (
              <ThreadView
                thread={thread}
                messages={messages}
                loading={loadingThread}
                onBack={() => setSelectedId(null)}
                onReplied={() => loadThread(selectedId)}
                currentUserId={user?.user_id}
              />
            )}
          </Card>
        </div>
      </div>

      {composeOpen && canCompose && (
        <ComposeDialog onClose={() => setComposeOpen(false)} onSent={handleSent} />
      )}
    </PageLayout>
  );
};


const ThreadView = ({ thread, messages, loading, onBack, onReplied, currentUserId }) => {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  // Reset reply when thread changes
  useEffect(() => { setReply(""); }, [thread?.thread_id]);

  const send = async () => {
    const html = (reply || "").trim();
    if (!html || html === "<br>" || html.replace(/<[^>]*>/g, "").trim() === "") {
      toast.error("Write a message first");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/messages/threads/${thread.thread_id}/reply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body_html: html }),
      });
      if (!res.ok) throw new Error(await res.text());
      setReply("");
      onReplied();
      toast.success("Reply sent");
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading || !thread) {
    return (
      <CardContent className="p-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…
      </CardContent>
    );
  }

  return (
    <>
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-3">
        <button onClick={onBack} className="md:hidden p-1 text-slate-500 hover:text-slate-900" data-testid="thread-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-slate-900 truncate">{thread.subject}</p>
          <p className="text-xs text-slate-500 truncate">
            {thread.other_participants?.map((p) => `${p.name || p.email}${p.role ? ` (${p.role})` : ""}`).join(", ") || "—"}
            {thread.participants && thread.participants.length > 2 && ` · ${thread.participants.length} participants`}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 max-h-[55vh]" data-testid="thread-messages">
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div key={m.message_id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.message_id}`}>
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarFallback className="bg-[#1E3A8A] text-white text-[11px]">{(m.sender_name || "?").charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className={`max-w-[80%] ${mine ? "items-end" : ""}`}>
                <div className={`flex items-center gap-2 mb-1 ${mine ? "justify-end" : ""}`}>
                  <span className="text-xs font-semibold text-slate-700">{mine ? "You" : m.sender_name}</span>
                  {!mine && m.sender_role && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{m.sender_role}</span>
                  )}
                  <span className="text-[10px] text-slate-400">{fmtFull(m.created_at)}</span>
                </div>
                <div
                  className={`prose prose-sm max-w-none rounded-2xl px-4 py-2.5 ${
                    mine ? "bg-[#1E3A8A] text-white prose-invert" : "bg-slate-50 text-slate-800"
                  } [&_a]:underline`}
                  dangerouslySetInnerHTML={{ __html: m.body_html }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-100 p-4 space-y-2">
        <RichTextEditor value={reply} onChange={setReply} placeholder="Write a reply…" testid="reply-editor" />
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending} className="btn-primary" data-testid="send-reply-btn">
            {sending ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-1.5" /> Send</>)}
          </Button>
        </div>
      </div>
    </>
  );
};


const ComposeDialog = ({ onClose, onSent }) => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState(null);   // cohort object
  const [expandedCohort, setExpandedCohort] = useState(null);
  const [pickedUserIds, setPickedUserIds] = useState([]);       // explicit student picks
  const [recipientMode, setRecipientMode] = useState("cohort"); // 'cohort' | 'select'
  const [asGroup, setAsGroup] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/messages/recipients`, { credentials: "include" });
        if (!r.ok) throw new Error("failed");
        const d = await r.json();
        setCohorts(d.cohorts || []);
      } catch (e) {
        toast.error("Couldn't load cohorts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredCohorts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cohorts;
    return cohorts.filter((c) =>
      (c.program_name || "").toLowerCase().includes(q) ||
      (c.cohort_label || "").toLowerCase().includes(q) ||
      (c.students || []).some((s) => (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q))
    );
  }, [cohorts, search]);

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject required"); return; }
    if (!body || body.replace(/<[^>]*>/g, "").trim() === "") { toast.error("Body required"); return; }

    let payload = { subject: subject.trim(), body_html: body, as_group: asGroup };
    if (recipientMode === "cohort") {
      if (!selectedCohort) { toast.error("Pick a cohort"); return; }
      payload.cohort_id = selectedCohort.cohort_id;
    } else {
      if (pickedUserIds.length === 0) { toast.error("Pick at least one student"); return; }
      payload.recipient_user_ids = pickedUserIds;
    }

    setSending(true);
    try {
      const r = await fetch(`${API}/messages/threads`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Send failed");
      }
      const d = await r.json();
      toast.success(`Message sent (${d.created} thread${d.created === 1 ? "" : "s"})`);
      onSent();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSending(false);
    }
  };

  const togglePick = (uid) => {
    setPickedUserIds((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-white border-slate-200" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <PencilLine className="w-5 h-5 text-[#1E3A8A]" /> New Message
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Pick a whole cohort or individual students, then write your message.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-[1fr_1.1fr] gap-4">
          {/* Left: recipient picker */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs">
              <button
                onClick={() => setRecipientMode("cohort")}
                className={`px-3 py-1.5 rounded-full border ${recipientMode === "cohort" ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}
                data-testid="mode-cohort-btn"
              >
                <UsersIcon className="w-3.5 h-3.5 inline mr-1" /> Whole cohort
              </button>
              <button
                onClick={() => setRecipientMode("select")}
                className={`px-3 py-1.5 rounded-full border ${recipientMode === "select" ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}
                data-testid="mode-select-btn"
              >
                Pick students
              </button>
            </div>

            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search cohorts or students…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 bg-white border-slate-200"
                data-testid="recipient-search"
              />
            </div>

            <div className="border border-slate-200 rounded-xl max-h-[42vh] overflow-y-auto bg-slate-50" data-testid="cohort-picker">
              {loading ? (
                <div className="p-6 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading…</div>
              ) : filteredCohorts.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">No cohorts found.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredCohorts.map((c) => {
                    const isExp = expandedCohort === c.cohort_id;
                    const isSelected = recipientMode === "cohort" && selectedCohort?.cohort_id === c.cohort_id;
                    return (
                      <li key={c.cohort_id}>
                        <div className={`px-3 py-2 flex items-center gap-2 ${isSelected ? "bg-[#FFD507]/15" : ""}`}>
                          <button onClick={() => setExpandedCohort(isExp ? null : c.cohort_id)} className="text-slate-400 hover:text-slate-700" data-testid={`expand-${c.cohort_id}`}>
                            {isExp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => {
                              if (recipientMode === "cohort") setSelectedCohort(c);
                              else setExpandedCohort(isExp ? null : c.cohort_id);
                            }}
                            className="flex-1 text-left"
                            data-testid={`cohort-row-${c.cohort_id}`}
                          >
                            <p className="font-semibold text-slate-900 text-sm">{c.program_name}</p>
                            <p className="text-xs text-slate-500">{c.cohort_label} · {c.student_count} students</p>
                          </button>
                          {recipientMode === "cohort" && (
                            <input
                              type="radio"
                              checked={isSelected}
                              onChange={() => setSelectedCohort(c)}
                              data-testid={`pick-cohort-${c.cohort_id}`}
                            />
                          )}
                        </div>
                        {isExp && (c.students?.length ? (
                          <ul className="bg-white border-t border-slate-100">
                            {c.students.map((s) => (
                              <li key={s.user_id} className="px-3 py-1.5 flex items-center gap-2 hover:bg-slate-50">
                                {recipientMode === "select" && (
                                  <Checkbox
                                    checked={pickedUserIds.includes(s.user_id)}
                                    onCheckedChange={() => togglePick(s.user_id)}
                                    data-testid={`pick-${s.user_id}`}
                                  />
                                )}
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={s.picture} />
                                  <AvatarFallback className="text-[10px] bg-slate-200 text-slate-700">{(s.name || s.email || "?").charAt(0).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium text-slate-800 truncate">{s.name || s.email}</p>
                                  <p className="text-[10px] text-slate-400 truncate">{s.email}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="px-3 py-2 text-xs text-slate-400 bg-white">No students enrolled.</p>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {recipientMode === "select" && pickedUserIds.length > 0 && (
              <p className="text-xs text-slate-500" data-testid="pick-summary">
                Selected: {pickedUserIds.length} student{pickedUserIds.length === 1 ? "" : "s"}
              </p>
            )}
            {recipientMode === "cohort" && selectedCohort && (
              <p className="text-xs text-slate-500" data-testid="cohort-summary">
                Target: <strong>{selectedCohort.program_name} · {selectedCohort.cohort_label}</strong> ({selectedCohort.student_count} students)
              </p>
            )}

            <label className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Checkbox checked={asGroup} onCheckedChange={(v) => setAsGroup(!!v)} data-testid="as-group-toggle" />
              <span>
                <strong>Send as group thread</strong> — everyone in one conversation (they see each other's replies).
                Uncheck to send individual 1:1 threads so replies stay private.
              </span>
            </label>
          </div>

          {/* Right: message body */}
          <div className="space-y-3">
            <Input
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-white border-slate-200"
              data-testid="compose-subject"
            />
            <RichTextEditor value={body} onChange={setBody} placeholder="Write your message…" testid="compose-editor" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compose-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending} className="btn-primary" data-testid="compose-send-btn">
            {sending ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-1.5" /> Send</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Messages;
